/*
 * MSG — the app's own message box (the replacement for window.alert).
 *
 * WHY: a native alert is a browser chrome dialog. It says "localhost:8080" above the text, it
 * wears the OS button, it cannot be styled, and it BLOCKS the JS thread — which in this app also
 * freezes every in-flight animation (the undo peel, the tray fly frame, the plant pulses). It
 * reads as the page having crashed rather than as the app telling you something.
 *
 * This is a real modal, unlike LrnDialog (dialog.js), which is deliberately anchored and
 * backdrop-less: an alert has no anchor — it is not ABOUT a control, it is about what just
 * happened — and it must be answered before the click that caused it can be repeated.
 *
 * CHASSIS: PL.layers owns dismissal (Esc + outside pointerdown; the backdrop counts as outside,
 * so a press anywhere off the card closes it and — since the backdrop swallows the press — does
 * NOT also land on the page beneath). PL.focus hands focus back to whatever the opener had.
 *
 * QUEUE, not stack: two messages fired from one handler show one after the other. Modals stacked
 * on modals put a backdrop over a backdrop and leave the user pressing Esc to count them down.
 *
 * TEXT IS PLAIN TEXT. Callers pass resource strings and, in the ajax error path, text that came
 * from the server — so it goes in via textContent and the CSS renders the "\n"s that several of
 * those strings carry (white-space: pre-line). Nothing here interpolates HTML.
 */
var MSG;

(function () {

	var queue = [];        // messages waiting behind the open one
	var openLayer = null;  // PL.layers handle while a box is up
	var focusToken = null;

	function buildBox(text, opts) {
		var overlay = document.createElement("div");
		overlay.className = "msgOverlayClass";

		var box = document.createElement("div");
		box.className = "msgBoxClass";
		box.setAttribute("role", "alertdialog");
		box.setAttribute("aria-modal", "true");

		if (opts.title) {
			var title = document.createElement("div");
			title.className = "msgBoxTitleClass";
			title.textContent = opts.title;
			box.appendChild(title);
		}

		var body = document.createElement("div");
		body.className = "msgBoxTextClass";
		body.textContent = text;
		box.appendChild(body);

		var foot = document.createElement("div");
		foot.className = "msgBoxFootClass";
		var ok = document.createElement("button");
		ok.type = "button";
		ok.className = "pl-btn-pill msgBoxOkClass";
		ok.textContent = getRes("msgOk_button") || "OK";
		foot.appendChild(ok);
		box.appendChild(foot);

		overlay.appendChild(box);
		return { overlay: overlay, box: box, ok: ok };
	}

	function showNext() {
		if (openLayer || !queue.length) { return; }
		var msg = queue.shift();
		var parts = buildBox(msg.text, msg.opts);

		focusToken = PL.focus.remember();
		document.body.appendChild(parts.overlay);

		// The layer element is the CARD, not the overlay: a press on the backdrop must read as
		// "outside" so it dismisses. The backdrop itself stops the press reaching the page.
		openLayer = PL.layers.push({
			el: parts.box,
			onClose: function () {
				openLayer = null;
				parts.overlay.remove();
				if (focusToken) {
					focusToken.restore({ ifWithin: parts.box });
					focusToken = null;
				}
				if (msg.opts.onClose) { msg.opts.onClose(); }
				showNext();   // whatever queued up behind this one
			}
		});

		parts.ok.addEventListener("click", function () {
			if (openLayer) { openLayer.close("api"); }
		});

		// The focus TRAP the chassis deliberately left unwritten (see js/pl/focus.js — the app had
		// no modal surface until this one). It is one line here rather than a general facility
		// because this modal has exactly ONE focusable control: Tab and Shift-Tab have nowhere
		// legitimate to go, so both simply stay put instead of walking into the page behind the
		// backdrop, where the user can see the controls but must not reach them.
		parts.box.addEventListener("keydown", function (e) {
			if (e.key === "Tab") { e.preventDefault(); parts.ok.focus(); }
		});

		// Enter/Space answer the box without reaching for the mouse — the one thing a native
		// alert did get right.
		parts.ok.focus();
	}

	// ── TOAST — the non-modal half of the same job ──────────────────────────────────────────────
	// A modal is the right shape for "this did not work" and for anything the user must answer
	// before repeating the click that caused it. It is the WRONG shape for "that worked": a
	// confirmation the user already expects should not take the focus, dim the page and demand an
	// OK — it should say so and get out of the way.
	//
	// Lives here rather than in a view module because it is the same decision as showMessage, taken
	// the other way. The Learn session grew one of these first (#lrnToastId); this is that widget
	// promoted, and learning_session.js now calls through to it — one toast in the app, one place
	// to change its dwell or its look.
	//
	// ONE toast element, reused: a second call REPLACES the text and restarts the clock rather than
	// stacking, so a burst of adds leaves the last word on screen instead of a pile.
	var TOAST_MS = 2600;
	var toastEl = null, toastTimer = null;

	function showToast(text, mark) {
		if (!toastEl) {
			toastEl = document.createElement("div");
			toastEl.id = "plToastId";
			// Announced by screen readers without stealing focus — the whole point of a toast.
			toastEl.setAttribute("role", "status");
			toastEl.setAttribute("aria-live", "polite");
			document.body.appendChild(toastEl);
		}
		toastEl.textContent = "";
		var span = document.createElement("span");
		span.textContent = String(text == null ? "" : text);
		toastEl.appendChild(span);
		// `mark` is an optional GARDEN.markIcon name (the Learn toast ends in the sprout). That
		// helper returns MARKUP and is the app's own, not caller text — the plain-text rule above
		// still governs everything that came from a resource string or the server.
		if (mark && window.GARDEN) {
			var holder = document.createElement("span");
			holder.innerHTML = GARDEN.markIcon(mark);
			while (holder.firstChild) { toastEl.appendChild(holder.firstChild); }
		}
		toastEl.classList.add("plToastShowClass");
		clearTimeout(toastTimer);
		toastTimer = setTimeout(function () {
			toastEl.classList.remove("plToastShowClass");
		}, TOAST_MS);
	}

	MSG = {
		/**
		 * Show a message. `opts`: { title?: string, onClose?: fn }.
		 * Returns immediately — unlike alert(), NOTHING after the call waits for the answer.
		 * Every current call site already treated the alert as terminal (it returns or falls
		 * out of the handler right after); a caller that needs to act on the dismissal passes
		 * onClose.
		 */
		show: function (text, opts) {
			queue.push({ text: String(text == null ? "" : text), opts: opts || {} });
			showNext();
		},

		/**
		 * Say something and fade out — no focus, no OK, no backdrop. For confirmations of an
		 * action the user just took and already expects to have worked. `mark` is an optional
		 * GARDEN.markIcon name shown after the text.
		 */
		toast: function (text, mark) { showToast(text, mark); }
	};

	// The name the call sites use, so the diff against the alert() they replace reads as a
	// one-word swap rather than a restructure.
	window.showMessage = function (text, opts) {
		if (window.VISIT_LOG) { VISIT_LOG.msg(text); }   // the key, never the text (visitor-click-recording-plan.md §3.4)
		MSG.show(text, opts);
	};
	window.showToast = function (text, mark) { MSG.toast(text, mark); };

})();
